import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/http/client';
import type {
  CreateSavedFilterViewRequest,
  SavedFilterView,
  SavedFilterViewWithWarnings,
  UpdateSavedFilterViewRequest,
} from '@/types/savedFilterView';

/**
 * Response envelope for the LIST endpoint. The backend returns either a
 * bare array or a DRF page; this client targets the bare-array shape
 * declared by the spec. If the backend later paginates, swap to
 * `fetchAllPages` in the same shape as `frontend/src/api/reports.ts`.
 */
type SavedFilterViewsListResponse = SavedFilterView[];

/** GET /api/orgs/{orgId}/saved-filter-views/?target_entity_type=<t> */
export async function fetchSavedFilterViews(
  orgId: number,
  targetEntityType: string,
  signal?: AbortSignal
): Promise<SavedFilterView[]> {
  return apiGet<SavedFilterViewsListResponse>(
    `/api/orgs/${orgId}/saved-filter-views/`,
    { target_entity_type: targetEntityType },
    10_000,
    signal
  );
}

/** GET /api/orgs/{orgId}/saved-filter-views/{viewId}/ — returns view with warnings */
export async function fetchSavedFilterView(
  orgId: number,
  viewId: string,
  signal?: AbortSignal
): Promise<SavedFilterViewWithWarnings> {
  return apiGet<SavedFilterViewWithWarnings>(
    `/api/orgs/${orgId}/saved-filter-views/${encodeURIComponent(viewId)}/`,
    undefined,
    10_000,
    signal
  );
}

/** POST /api/orgs/{orgId}/saved-filter-views/ */
export async function createSavedFilterView(
  orgId: number,
  body: CreateSavedFilterViewRequest
): Promise<SavedFilterView> {
  return apiPost<SavedFilterView>(
    `/api/orgs/${orgId}/saved-filter-views/`,
    body,
    10_000
  );
}

/** PATCH /api/orgs/{orgId}/saved-filter-views/{viewId}/ */
export async function updateSavedFilterView(
  orgId: number,
  viewId: string,
  body: UpdateSavedFilterViewRequest
): Promise<SavedFilterView> {
  return apiPatch<SavedFilterView>(
    `/api/orgs/${orgId}/saved-filter-views/${encodeURIComponent(viewId)}/`,
    body,
    10_000
  );
}

/** DELETE /api/orgs/{orgId}/saved-filter-views/{viewId}/ */
export async function deleteSavedFilterView(
  orgId: number,
  viewId: string
): Promise<void> {
  await apiDelete<unknown>(
    `/api/orgs/${orgId}/saved-filter-views/${encodeURIComponent(viewId)}/`,
    10_000
  );
}
